const cards = [
  {
    number: "01",
    title: "Executive Overview",
    description:
      "A single score that tells you where your hotel stands across AEO, GEO, AIO (AI Overviews), and SXO — gated by AI Crawlability, with AI Integration as an advisory extra — and the top issues to fix ranked by impact.",
    icon: "dashboard_customize",
    colSpan: "md:col-span-8",
    variant: "default" as const,
    hasImage: true,
  },
  {
    number: "02",
    title: "Trust Signals",
    description:
      "How search engines and AI models evaluate your credibility: review presence, secure booking flows, schema authority markers, and E-E-A-T compliance.",
    icon: "verified_user",
    colSpan: "md:col-span-4",
    variant: "tertiary" as const,
  },
  {
    number: "03",
    title: "Detailed Analysis",
    description:
      "Category-by-category breakdown with specific scores for Answer Engine readiness, Generative Engine compatibility, AI bot accessibility, and search experience.",
    icon: "query_stats",
    colSpan: "md:col-span-4",
    variant: "secondary" as const,
    hasProgress: true,
  },
  {
    number: "04",
    title: "Actionable Recommendations",
    description:
      "Prioritized fixes with step-by-step implementation guides and AI-powered code suggestions you can deploy immediately.",
    icon: "bolt",
    colSpan: "md:col-span-4",
    variant: "primary" as const,
  },
  {
    number: "05",
    title: "AI Intelligence Guide",
    description:
      "A hands-on playbook for allowing AI crawlers, structuring content for LLM consumption, and tracking AI-referred traffic to your booking engine.",
    icon: "auto_awesome",
    colSpan: "md:col-span-4",
    variant: "dark" as const,
  },
];

const getCardStyles = (variant: string) => {
  switch (variant) {
    case "primary":
      return "bg-primary text-on-primary";
    case "dark":
      return "bg-inverse-surface text-inverse-on-surface";
    default:
      return "bg-surface-container-lowest text-on-surface";
  }
};

const getIconContainerStyles = (variant: string) => {
  switch (variant) {
    case "primary":
      return "bg-on-primary/10";
    case "dark":
      return "bg-surface-tint/20";
    case "tertiary":
      return "bg-tertiary-fixed";
    case "secondary":
      return "bg-secondary-container";
    default:
      return "bg-primary-container/10";
  }
};

const getIconStyles = (variant: string) => {
  switch (variant) {
    case "primary":
      return "text-on-primary";
    case "dark":
      return "text-surface-tint";
    case "tertiary":
      return "text-tertiary";
    case "secondary":
      return "text-secondary";
    default:
      return "text-primary";
  }
};

const getNumberStyles = (variant: string) => {
  switch (variant) {
    case "primary":
      return "text-on-primary/20";
    case "dark":
      return "text-surface-variant/10";
    default:
      return "text-outline-variant/20";
  }
};

const getDescriptionStyles = (variant: string) => {
  switch (variant) {
    case "primary":
      return "text-on-primary/80";
    case "dark":
      return "text-inverse-on-surface/70";
    default:
      return "text-on-surface-variant";
  }
};

const ReportArchitectureGrid = () => {
  return (
    <section className="py-24 px-8 bg-surface-container-low">
      <div className="max-w-8xl mx-auto">
        <div className="mb-16">
          <h2 className="font-headline text-4xl font-extrabold mb-4">What Your Audit Uncovers</h2>
          <p className="text-on-surface-variant max-w-2xl">
            Every audit scores your hotel across five critical dimensions of modern search — from
            structured data and schema markup to how AI models interpret your content.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {cards.map((card) => (
            <div
              key={card.number}
              className={`${card.colSpan} ${getCardStyles(card.variant)} p-10 rounded-3xl group hover:shadow-2xl transition-all duration-500`}
            >
              <div className="flex justify-between items-start mb-8">
                <div className={`${getIconContainerStyles(card.variant)} p-4 rounded-2xl`}>
                  <span
                    className={`material-symbols-outlined ${getIconStyles(card.variant)} text-3xl`}
                  >
                    {card.icon}
                  </span>
                </div>
                <span className={`text-4xl font-black ${getNumberStyles(card.variant)}`}>
                  {card.number}
                </span>
              </div>

              <h3
                className={`font-headline ${card.colSpan.includes("8") ? "text-3xl" : "text-2xl"} font-bold mb-4`}
              >
                {card.title}
              </h3>
              <p
                className={`${getDescriptionStyles(card.variant)} ${card.colSpan.includes("8") ? "text-lg" : ""} leading-relaxed ${card.hasImage ? "mb-8" : ""}`}
              >
                {card.description}
              </p>

              {card.hasImage && (
                <img
                  src="./Landing_Page_Image_1.png"
                  alt="Abstract data visualization showing growth trends"
                  className="w-full h-48 object-cover rounded-2xl"
                  loading="lazy"
                  width={800}
                  height={300}
                />
              )}

              {card.hasProgress && (
                <div className="space-y-3 mt-6">
                  <div className="h-2 w-full bg-surface-container-high rounded-full overflow-hidden">
                    <div className="h-full bg-secondary w-3/4" />
                  </div>
                  <div className="h-2 w-full bg-surface-container-high rounded-full overflow-hidden">
                    <div className="h-full bg-secondary w-1/2" />
                  </div>
                </div>
              )}

              {card.variant === "tertiary" && (
                <div className="mt-8 pt-8 border-t border-surface-container-low">
                  <div className="flex gap-2">
                    <div className="h-8 w-8 rounded-full bg-surface-container-high" />
                    <div className="h-8 w-8 rounded-full bg-surface-container-high" />
                    <div className="h-8 w-8 rounded-full bg-surface-container-high" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReportArchitectureGrid;
